import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { msalConfig } from '../config/authConfig';

/**
 * Hook for authentication operations.
 * Auth is B2C-only; logout ends B2C session and clears local state.
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { instance } = useMsal();
  const { logout: logoutStore, user, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotificationStore();

  // Get current user query
  // Silently handle errors when backend is unavailable
  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authService.getCurrentUser(),
    enabled: isAuthenticated,
    retry: false,
  });

  // Logout: clear local state and redirect to B2C logout (then user lands on app and gets redirected to B2C login)
  const logout = () => {
    logoutStore();
    useContextStore.getState().clearContext();
    queryClient.clear();
    addNotification('Logged out successfully', 'info');
    if (instance) {
      instance
        .logoutRedirect({
          postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri ?? window.location.origin,
        })
        .catch(() => {
          navigate('/');
        });
    } else {
      navigate('/');
    }
  };

  return {
    user,
    currentUser,
    isAuthenticated,
    isLoadingUser,
    logout,
  };
};
